import React, { useState, useEffect } from "react";

/**
 * Google Ads AI Campaign preferences — service mode, budget guardrails, campaign preferences.
 * Fetches/saves via /api/google_ads_ai/settings. No monthly budget cap.
 */
const CampaignPreferencesTab = ({ isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [formData, setFormData] = useState({
    service_mode: "self_managed",
    default_budget_per_event_cents: null,
    max_budget_per_event_cents: null,
    budget_guidelines: "",
    campaign_preferences: "",
    auto_generate_campaigns: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch("/api/google_ads_ai/settings", { headers: { Accept: "application/json" } });
      if (res.ok) {
        const data = await res.json();
        setFormData({
          service_mode: data.service_mode || "self_managed",
          default_budget_per_event_cents: data.default_budget_per_event_cents ?? null,
          max_budget_per_event_cents: data.max_budget_per_event_cents ?? null,
          budget_guidelines: data.budget_guidelines || "",
          campaign_preferences: data.campaign_preferences || "",
          auto_generate_campaigns: data.auto_generate_campaigns || false,
        });
      }
    } catch (e) {
      console.error("[Google Ads CampaignPreferences] Error fetching settings:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      const res = await fetch("/api/google_ads_ai/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf || "",
        },
        body: JSON.stringify({ settings: formData }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setSaveMessage({ type: "success", text: "Preferences saved successfully." });
      } else {
        setSaveMessage({ type: "error", text: (data.errors && data.errors.join(", ")) || data.error || "Failed to save preferences." });
      }
    } catch (e) {
      setSaveMessage({ type: "error", text: "Error saving preferences: " + e.message });
    } finally {
      setSaving(false);
    }
  };

  const centsToDollars = (cents) => {
    if (cents === null || cents === undefined || cents === "") return "";
    return (cents / 100).toString();
  };

  const dollarsToCents = (value) => {
    if (value === "" || value == null) return null;
    return Math.round(parseFloat(value) * 100);
  };

  const updateBudgetField = (field, dollarValue) => {
    setFormData((prev) => ({ ...prev, [field]: dollarsToCents(dollarValue) }));
  };

  if (loading) {
    return <div className="meta-ads-ai-page__pref-loading">Loading preferences…</div>;
  }

  return (
    <div className="meta-ads-ai-page__pref-container">
      <div className="meta-ads-ai-page__pref-section">
        <h4 className="meta-ads-ai-page__pref-section-title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Service Mode</span>
          {isAdmin && <span className="admin-only-dot" style={{ marginLeft: 0, flexShrink: 0 }} />}
        </h4>
        {isAdmin ? (
          <div className="meta-ads-ai-page__service-mode-options">
            <label className={`meta-ads-ai-page__service-mode-option ${formData.service_mode === "self_managed" ? "meta-ads-ai-page__service-mode-option--active" : ""}`}>
              <input
                type="radio"
                name="service_mode"
                value="self_managed"
                checked={formData.service_mode === "self_managed"}
                onChange={(e) => setFormData((prev) => ({ ...prev, service_mode: e.target.value }))}
              />
              <div>
                <strong>Self-Managed</strong>
                <p>Use Vesta AI to draft campaigns, then review, edit, and publish them yourself.</p>
              </div>
            </label>
            <label className={`meta-ads-ai-page__service-mode-option ${formData.service_mode === "vesta_managed" ? "meta-ads-ai-page__service-mode-option--active" : ""}`}>
              <input
                type="radio"
                name="service_mode"
                value="vesta_managed"
                checked={formData.service_mode === "vesta_managed"}
                onChange={(e) => setFormData((prev) => ({ ...prev, service_mode: e.target.value }))}
              />
              <div>
                <strong>Vesta Managed</strong>
                <p>Our digital advertising specialists use Vesta AI to create, optimize, and manage your campaigns for you.</p>
              </div>
            </label>
          </div>
        ) : (
          <div className={`meta-ads-ai-page__service-mode-badge meta-ads-ai-page__service-mode-badge--${formData.service_mode}`}>
            <span className="meta-ads-ai-page__service-mode-badge-text">
              {formData.service_mode === "vesta_managed" ? "Vesta Managed" : "Self-Managed"}
            </span>
            <p className="meta-ads-ai-page__service-mode-badge-desc">
              {formData.service_mode === "vesta_managed"
                ? "Our digital advertising specialists use Vesta AI to create, optimize, and manage your campaigns for you."
                : "Use Vesta AI to draft campaigns, then review, edit, and publish them yourself."}
            </p>
          </div>
        )}
      </div>

      <div className="meta-ads-ai-page__pref-section">
        <h4 className="meta-ads-ai-page__pref-section-title">Budget Guardrails</h4>
        <p className="meta-ads-ai-page__pref-description">
          Set budget boundaries that the AI will follow when generating campaign proposals.
        </p>
        <div className="meta-ads-ai-page__pref-grid">
          <div className="meta-ads-ai-page__pref-field">
            <label>Default Budget per Event</label>
            <div className="meta-ads-ai-page__pref-input-wrap">
              <span className="meta-ads-ai-page__pref-input-prefix">$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={centsToDollars(formData.default_budget_per_event_cents)}
                onChange={(e) => updateBudgetField("default_budget_per_event_cents", e.target.value)}
                placeholder="e.g. 200"
              />
            </div>
            <p className="meta-ads-ai-page__pref-hint">Suggested starting budget for each campaign</p>
          </div>
          <div className="meta-ads-ai-page__pref-field">
            <label>Maximum Budget per Event</label>
            <div className="meta-ads-ai-page__pref-input-wrap">
              <span className="meta-ads-ai-page__pref-input-prefix">$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={centsToDollars(formData.max_budget_per_event_cents)}
                onChange={(e) => updateBudgetField("max_budget_per_event_cents", e.target.value)}
                placeholder="e.g. 500"
              />
            </div>
            <p className="meta-ads-ai-page__pref-hint">Hard cap the AI will not exceed per campaign</p>
          </div>
        </div>
      </div>

      <div className="meta-ads-ai-page__pref-section">
        <h4 className="meta-ads-ai-page__pref-section-title">Budget Guidelines</h4>
        <p className="meta-ads-ai-page__pref-description">
          Provide any rules or preferences about how budgets should be allocated across your Google Ads campaigns.
        </p>
        <textarea
          className="meta-ads-ai-page__pref-textarea"
          value={formData.budget_guidelines}
          onChange={(e) => setFormData((prev) => ({ ...prev, budget_guidelines: e.target.value }))}
          placeholder={"Examples:\n- Never spend more than 20% of budget in the first 3 days\n- Prefer front-loading budget for events less than 2 weeks away\n- Reserve 10% of budget for last-minute boosts if ticket sales are slow"}
          rows={4}
        />
      </div>

      <div className="meta-ads-ai-page__pref-section">
        <h4 className="meta-ads-ai-page__pref-section-title">Campaign Preferences</h4>
        <p className="meta-ads-ai-page__pref-description">
          General guidelines for AI-generated Google Ads (Search) campaigns. These preferences inform how the AI drafts your campaigns.
        </p>
        <textarea
          className="meta-ads-ai-page__pref-textarea"
          value={formData.campaign_preferences}
          onChange={(e) => setFormData((prev) => ({ ...prev, campaign_preferences: e.target.value }))}
          placeholder="Examples:&#10;- Always include our brand tagline&#10;- Prefer exact match keywords for brand terms&#10;- Use conversational, friendly tone in ad copy&#10;- Target specific locations or audiences"
          rows={4}
        />
      </div>

      <div className="meta-ads-ai-page__pref-section meta-ads-ai-page__pref-section--last">
        <h4 className="meta-ads-ai-page__pref-section-title">Automatic Campaign Generation</h4>
        <label className="meta-ads-ai-page__pref-toggle-label">
          <input
            type="checkbox"
            checked={formData.auto_generate_campaigns}
            onChange={(e) => setFormData((prev) => ({ ...prev, auto_generate_campaigns: e.target.checked }))}
          />
          <span>Automatically generate AI draft campaigns for all new events</span>
        </label>
        <p className="meta-ads-ai-page__pref-hint">
          When enabled, Vesta AI will automatically create draft Google Ads (Search) campaign proposals for every new event.
          {formData.service_mode === "vesta_managed"
            ? " Our specialists will review and manage them for you."
            : " You can still review and edit before publishing."}
        </p>
      </div>

      <div className="meta-ads-ai-page__pref-actions">
        {saveMessage && (
          <div className={`meta-ads-ai-page__pref-alert meta-ads-ai-page__pref-alert--${saveMessage.type}`}>
            {saveMessage.text}
          </div>
        )}
        <button type="button" className="meta-ads-ai-page__pref-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Preferences"}
        </button>
      </div>
    </div>
  );
};

export default CampaignPreferencesTab;
